/**
 * Mock function that returns download content for TieOut results.
 * In the future, this will be replaced with a real API call.
 * @param {object} data - The data object with section info
 * @returns {object} - Object with html content
 */
export const getDownloadContent = async (data) => {
  // Mock result - returns a simple HTML representation of the tie-out results
  const section = data?.section || 'Unknown Section';
  const version = data?.version || 'Unknown Version';
  const status = data?.status || 'Unknown Status';
  const executionDate = data?.execution_date || data?.execution_completion || 'N/A';
  const executedBy = data?.executed_by || 'N/A';

  const html = `
    <h1>Tie-Out Results</h1>
    <h2>${section}</h2>
    <table>
      <thead>
        <tr>
          <th>Field</th>
          <th>Value</th>
        </tr>
      </thead>
      <tbody>
        <tr><td>Section</td><td>${section}</td></tr>
        <tr><td>Version</td><td>${version}</td></tr>
        <tr><td>Status</td><td>${status}</td></tr>
        <tr><td>Execution Date</td><td>${executionDate}</td></tr>
        <tr><td>Executed By</td><td>${executedBy}</td></tr>
      </tbody>
    </table>
    <h3>Summary</h3>
    <p>This is a mock tie-out result for the <strong>${section}</strong> section. 
    The tie-out was performed on the <strong>${version}</strong> version and the current status is <strong>${status}</strong>.</p>
    <h3>Findings</h3>
    <table>
      <thead>
        <tr>
          <th>No</th>
          <th>Description</th>
          <th>Severity</th>
          <th>Status</th>
        </tr>
      </thead>
      <tbody>
        <tr><td>1</td><td>All values reconciled within tolerance</td><td>No Issue</td><td>Passed</td></tr>
        <tr><td>2</td><td>Cross-reference check completed</td><td>No Issue</td><td>Passed</td></tr>
        <tr><td>3</td><td>Rounding differences within acceptable range</td><td>FYI</td><td>Reviewed</td></tr>
      </tbody>
    </table>
  `;

  return { html };
};

/**
 * Wraps HTML content with proper styling for PDF generation
 * @param {string} fullHtml - The HTML content to wrap
 * @param {object} data - Data object containing section info
 * @returns {string} - Formatted HTML with styles
 */
const beautifyWrapper = (fullHtml, data) => {
  const section = data?.section || 'Tie-Out';
  const version = data?.version || '';

  const formattedHtml = `
<!DOCTYPE html>
<html>
  <head>
    <meta charset="UTF-8">
    <title>TieOut ${section} ${version}</title>
    <style>
      @page {
        size: A4;
        margin: 10mm;
      }

      @media print {
        html, body {
          margin: 0 !important;
          padding: 0 !important;
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        }

        h1, h2, h3, h4, h5, h6 {
          page-break-after: avoid;
          break-after: avoid;
        }

        p, li, blockquote {
          page-break-inside: avoid;
          break-inside: avoid;
        }

        table {
          page-break-inside: auto;
          break-inside: auto;
        }

        tr {
          page-break-inside: avoid;
          break-inside: avoid;
        }

        thead {
          display: table-header-group;
        }

        tfoot {
          display: table-footer-group;
        }

        p {
          orphans: 3;
          widows: 3;
        }
      }

      * {
        margin: 0;
        padding: 0;
        box-sizing: border-box;
      }

      body {
        margin: 0;
        padding: 15mm;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Arial, sans-serif;
        line-height: 1.5;
        color: #333;
        font-size: 11px;
      }

      h1 {
        font-size: 20px;
        margin-bottom: 15px;
        color: #1a1a1a;
      }

      h2 {
        font-size: 16px;
        margin-top: 20px;
        margin-bottom: 10px;
        color: #2c3e50;
      }

      h3 {
        font-size: 14px;
        margin-top: 15px;
        margin-bottom: 8px;
        color: #34495e;
      }

      h4 {
        font-size: 12px;
        margin-top: 12px;
        margin-bottom: 6px;
      }

      p {
        margin-bottom: 10px;
      }

      ul, ol {
        margin: 10px 0 10px 20px;
      }

      li {
        margin-bottom: 4px;
      }

      table {
        border-collapse: collapse;
        width: 100%;
        margin: 15px 0;
        table-layout: fixed;
        font-size: 9px;
      }

      th {
        padding: 4px 6px;
        border: 1px solid #ddd;
        background-color: #f5f5f5;
        text-align: left;
        font-weight: 600;
        word-wrap: break-word;
        overflow-wrap: break-word;
      }

      td {
        padding: 4px 6px;
        border: 1px solid #ddd;
        text-align: left;
        word-wrap: break-word;
        overflow-wrap: break-word;
      }

      tr {
        border-bottom: 1px solid #ddd;
      }

      tr:nth-child(even) {
        background-color: #fafafa;
      }

      pre {
        background: #f6f8fa;
        padding: 10px;
        border-radius: 4px;
        overflow-x: auto;
        margin: 10px 0;
        border: 1px solid #e1e4e8;
        white-space: pre-wrap;
        word-wrap: break-word;
        font-size: 9px;
      }

      code {
        font-family: 'SF Mono', Consolas, 'Courier New', monospace;
        font-size: 9px;
        background: #f0f0f0;
        padding: 1px 4px;
        border-radius: 3px;
      }

      pre code {
        background: none;
        padding: 0;
      }

      blockquote {
        border-left: 3px solid #007bff;
        padding: 8px 15px;
        margin: 15px 0;
        background: #f8f9fa;
        color: #555;
      }

      hr {
        border: none;
        border-top: 1px solid #ddd;
        margin: 20px 0;
      }

      a {
        color: #007bff;
        text-decoration: none;
      }

      img {
        max-width: 100%;
        height: auto;
      }

      .page-break {
        page-break-before: always;
        break-before: always;
      }
    </style>
  </head>
  <body>
    ${fullHtml}
  </body>
</html>
`;

  return formattedHtml;
};

/**
 * Downloads results as a rich PDF using browser print dialog
 * @param {string} fullHtml - The HTML content to print
 * @param {object} data - Data object containing section info
 * @param {function} onComplete - Callback when download is complete
 */
const downloadRichPdf = (fullHtml, data, onComplete) => {
  const formattedHtml = beautifyWrapper(fullHtml, data);

  // Create hidden iframe
  const iframe = document.createElement('iframe');
  iframe.style.position = 'fixed';
  iframe.style.right = '0';
  iframe.style.bottom = '0';
  iframe.style.width = '0';
  iframe.style.height = '0';
  iframe.style.border = 'none';
  iframe.style.visibility = 'hidden';

  document.body.appendChild(iframe);
  iframe.srcdoc = formattedHtml;

  iframe.onload = () => {
    setTimeout(() => {
      try {
        iframe.contentWindow.focus();
        iframe.contentWindow.addEventListener('afterprint', () => {
          if (document.body.contains(iframe)) {
            document.body.removeChild(iframe);
          }
          if (onComplete) onComplete();
        });
        iframe.contentWindow.print();
      } catch (e) {
        console.error('Print error:', e);
        if (document.body.contains(iframe)) {
          document.body.removeChild(iframe);
        }
        if (onComplete) onComplete();
      }
    }, 500);
  };
};

/**
 * Downloads TieOut results for a given data object
 * @param {object} data - The data object with section info
 * @param {function} onStart - Callback when download starts
 * @param {function} onComplete - Callback when download completes
 */
export const downloadTieOutResults = async (data, onStart, onComplete) => {
  if (onStart) onStart();

  try {
    const content = await getDownloadContent(data);

    if (!content?.html) {
      console.error('No result content found');
      if (onComplete) onComplete();
      return;
    }

    downloadRichPdf(content.html, data, onComplete);
  } catch (error) {
    console.error('Error downloading tie-out results:', error);
    if (onComplete) onComplete();
  }
};
